import type { ReactNode } from 'react';
import type { Deal } from './Deal';
import { matchDealType } from './Deal';

export const NUMBER_OF_UPLOAD_AT_ONCE = 3;
export const NUMBER_OF_DELETE_AT_ONCE = 10;

export type DealReportImage = {
  type: 'DealInspectionReportImage' | 'DealCompletionReportImage';
  id: number;
  dealId: number;
  seq: number;
  uploadedBy: 'company' | 'vendor';
  imageUrl: string;
  thumbnailImageUrl: string | null;
  point: string | null;
  note: string | null;
  shared: boolean;
  outputPdfImage: boolean;
  outputPdfNote: boolean;
  construction: 'unspecified' | 'targeted';
  createdAt: string;
};

export type DealInspectionReportImage = DealReportImage & {
  type: 'DealInspectionReportImage';
};

export type DealCompletionReportImage = DealReportImage & {
  type: 'DealCompletionReportImage';
};

export type DealReportBulkUpdateParams = {
  shared?: boolean;
  outputPdfImage?: boolean;
  outputPdfNote?: boolean;
  construction?: 'unspecified' | 'targeted';
};

export const navigationTitle = (deal: Deal): string =>
  `${
    matchDealType(deal, 'repair') ? '現地調査の' : '立会報告'
  }画像を登録してください`;

export type ReportImageBulkAction = {
  children: string;
  danger?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  onClick: () => void | Promise<void>;
};

export const imageUploadedLabel = (
  isVendor: boolean,
  reportImage: DealReportImage
): string => {
  if (isVendor && reportImage.uploadedBy === 'company') return '管理会社登録';
  if (!isVendor && reportImage.uploadedBy === 'vendor') return '施工会社登録';
  return '';
};

/** 指定した companyType がアップロードした画像かどうか */
export const operableReportImage = (
  dealReportImage: DealReportImage,
  companyType: 'company' | 'vendor'
): boolean => dealReportImage.uploadedBy === companyType;

/** 選択中の画像のうち、自社がアップロードした画像のみを返す */
export const filterSelectedOwnImages = (
  reportImages: DealReportImage[],
  selectedImageIds: ReadonlyArray<number>,
  companyType: 'company' | 'vendor'
): DealReportImage[] =>
  reportImages.filter(
    (reportImage) =>
      selectedImageIds.includes(reportImage.id) &&
      reportImage.uploadedBy === companyType
  );

/** 選択中の画像の中に操作可能な画像が1つでも含まれているかどうか */
export const hasOperableSelectedImages = (
  reportImages: DealReportImage[],
  selectedImageIds: ReadonlyArray<number>,
  companyType: 'company' | 'vendor'
): boolean =>
  reportImages
    .filter((reportImage) => selectedImageIds.includes(reportImage.id))
    .some((reportImage) => operableReportImage(reportImage, companyType));

/** 報告書に出力できる画像があるかどうか */
export const imagesExistInReport = (
  orderImages: DealReportImage[],
  isVendor: boolean
): boolean => {
  if (isVendor) {
    return orderImages.length > 0;
  }
  return orderImages.some((img) => img.outputPdfImage);
};
